/* eslint-disable jsdoc/require-jsdoc -- 私有校验辅助函数由导出校验器说明语义。 */
import { isHttpClientError } from "../../../lib/http/errors";
import {
  createStayResource,
  createStayResourceMedia,
  createStayResourceSku,
  createStayResourceSupplier,
  deleteStayResource,
  fetchStayResource,
  fetchStayResourceGeoCandidates,
  fetchStayResourceGeoReviews,
  fetchStayResourceHousLevels,
  fetchStayResourceLegacyMappings,
  fetchStayResourceMedia,
  fetchStayResources,
  fetchStayResourceSkus,
  fetchStayResourceSuppliers,
  fetchStayResourceSupplierOptions,
  obsoleteStayResourceMedia,
  reorderStayResourceMedia,
  reviewStayResource,
  reviewStayResourceGeo,
  setStayResourceDisabled,
  setStayResourceSkuDisabled,
  updateStayResource,
  updateStayResourceMedia,
  updateStayResourceSku,
  updateStayResourceSupplier,
  uploadStayResourceMediaFile,
  verifyStayResourceGeo,
} from "../repo";
import {
  getCityOptions,
  getDistrictOptions,
  getProvinceOptions,
} from "../../../shared/region";
import type {
  StayResourceGeoCandidate,
  StayResourceGeoReviewMutation,
  StayResourceGeoVerificationResult,
  StayResourceMediaItem,
  StayResourceMediaMutation,
  StayResourceReviewMutation,
  StayResourceSkuItem,
  StayResourceSkuMutation,
  StayResourceSpuItem,
  StayResourceSpuMutation,
  StayResourceSupplierMutation,
  StayResourceSupplierItem,
  StayResourceSupplierReviewMutation,
} from "../types";

/** 多个同名分店且未填括号分店时，新增链路的稳定中文失败提示。 */
export const MULTI_SAME_HOTEL_GEO_CREATE_ERROR =
  "该城市存在多个同名分店，请在酒店名称括号中填写分店（如：如家酒店(火车站店)）";

/** 高德未在所选省市定位到该分店时，新增链路的稳定中文失败提示。 */
export const GEO_REGION_MISMATCH_CREATE_ERROR =
  "未在所选省市定位到该酒店分店，请核对省份、城市或酒店分店名称";

/** 去掉括号门店后的酒店主名，用于判断是否同一连锁店。 */
export function stripHotelBranchName(name: string): string {
  return name.replace(/[（(][^）)]*[）)]/g, "").replace(/\s+/g, "").trim();
}

/** 酒店名称是否已包含括号门店信息。 */
export function hasHotelBranchName(hotelName: string): boolean {
  return /[（(][^）)]+[）)]/.test(hotelName.trim());
}

/** 提取括号分店名核心文案，例如「金科中心」。 */
export function hotelBranchLabel(hotelName: string): string | null {
  const match = hotelName.trim().match(/[（(]([^（）()]{1,40})店[）)]/);
  const label = match?.[1]?.trim();
  return label || null;
}

/** 高德返回多条候选时视为分店歧义（名称写法常不一致，不能只比去掉括号后的主名）。 */
export function areMultiSameHotelCandidates(
  candidates: Array<Pick<StayResourceGeoCandidate, "name">>,
): boolean {
  return candidates.length > 1;
}

/** 候选名称是否包含去掉括号后的酒店主名（用于区分同城无关门店）。 */
export function candidateMatchesHotelCoreName(
  candidateName: string,
  hotelName: string,
): boolean {
  const core = stripHotelBranchName(hotelName);
  if (!core) return false;
  return candidateName.replace(/\s+/g, "").includes(core);
}

/** 所选省市下、名称与酒店主名相关的候选。 */
export function filterRegionNameMatchedCandidates(
  payload: Pick<StayResourceSpuMutation, "hotelName" | "province" | "city" | "district">,
  candidates: StayResourceGeoCandidate[],
): StayResourceGeoCandidate[] {
  return candidates.filter((candidate) =>
    candidateMatchesManualRegion(candidate, payload)
    && candidateMatchesHotelCoreName(candidate.name, payload.hotelName));
}

/** 未填括号分店且同城同名相关候选多于一条时阻断；已填分店则允许落成待审核。 */
export function shouldRejectMultiSameHotelGeo(
  payload: StayResourceSpuMutation,
  geo: Pick<StayResourceGeoVerificationResult, "candidates">,
): boolean {
  if (hasHotelBranchName(payload.hotelName)) return false;
  return areMultiSameHotelCandidates(filterRegionNameMatchedCandidates(payload, geo.candidates));
}

/** 候选省、市是否与手填一致；手填了区县时区县也必须一致。 */
export function candidateMatchesManualRegion(
  candidate: Pick<StayResourceGeoCandidate, "province" | "city" | "district">,
  payload: Pick<StayResourceSpuMutation, "province" | "city" | "district">,
): boolean {
  if (candidate.province.trim() !== payload.province.trim()) return false;
  if (candidate.city.trim() !== payload.city.trim()) return false;
  const expectedDistrict = payload.district?.trim() ?? "";
  if (expectedDistrict && (candidate.district ?? "").trim() !== expectedDistrict) {
    return false;
  }
  return true;
}

/** 候选名称是否包含分店关键词（有分店名时用于排除同城无关门店）。 */
export function candidateMatchesHotelBranch(
  candidateName: string,
  hotelName: string,
): boolean {
  const branch = hotelBranchLabel(hotelName);
  if (!branch) return true;
  return candidateName.replace(/\s+/g, "").includes(branch.replace(/\s+/g, ""));
}

/**
 * 新增时必须能在所选省市落到该酒店：
 * - 无候选 / 候选省市全不匹配 → 阻断
 * - 未填分店时，同省市还必须存在名称含酒店主名的候选 → 否则按省市不一致阻断
 * - 已填分店时，同省市候选还必须名称含分店关键词 → 否则阻断
 */
export function shouldRejectGeoRegionMismatch(
  payload: StayResourceSpuMutation,
  geo: Pick<StayResourceGeoVerificationResult, "candidates">,
): boolean {
  const regionMatched = geo.candidates.filter((candidate) =>
    candidateMatchesManualRegion(candidate, payload));
  if (regionMatched.length === 0) return true;
  if (!hasHotelBranchName(payload.hotelName)) {
    return !regionMatched.some((candidate) =>
      candidateMatchesHotelCoreName(candidate.name, payload.hotelName));
  }
  return !regionMatched.some((candidate) =>
    candidateMatchesHotelBranch(candidate.name, payload.hotelName));
}
export {
  createStayResource,
  createStayResourceMedia,
  createStayResourceSku,
  createStayResourceSupplier,
  deleteStayResource,
  fetchStayResource as loadStayResource,
  fetchStayResourceGeoCandidates as loadStayResourceGeoCandidates,
  fetchStayResourceGeoReviews as loadStayResourceGeoReviews,
  fetchStayResourceHousLevels as loadStayResourceHousLevels,
  fetchStayResourceLegacyMappings as loadStayResourceLegacyMappings,
  fetchStayResourceMedia as loadStayResourceMedia,
  fetchStayResources as loadStayResources,
  fetchStayResourceSkus as loadStayResourceSkus,
  fetchStayResourceSuppliers as loadStayResourceSuppliers,
  fetchStayResourceSupplierOptions as loadStayResourceSupplierOptions,
  obsoleteStayResourceMedia,
  reorderStayResourceMedia,
  reviewStayResource,
  reviewStayResourceGeo,
  setStayResourceDisabled,
  setStayResourceSkuDisabled,
  updateStayResource,
  updateStayResourceMedia,
  updateStayResourceSku,
  updateStayResourceSupplier,
  uploadStayResourceMediaFile,
  verifyStayResourceGeo,
};

export type StayResourceMutationErrors = Partial<Record<string, string>>;

/** 上传住宿媒体文件，元数据仍由房资源接口单独保存。 */
/** 将酒店详情转换为不包含坐标、价格或供应商字段的可编辑载荷。 */
export function toStayResourceSpuMutation(item: StayResourceSpuItem): StayResourceSpuMutation {
  return {
    publicPhones: item.publicPhones,
    roomCount: item.roomCount,
    openingYear: item.openingYear,
    renovationYear: item.renovationYear,
    platformRating: item.platformRating,
    hotelName: item.hotelName,
    hotelShortName: item.hotelShortName ?? "",
    country: item.country,
    province: item.province,
    city: item.city,
    district: item.district ?? "",
    address: item.address ?? "",
    stayLevel: item.stayLevel ?? "",
    hotelType: item.hotelType ?? "",
    remark: item.remark,
  };
}

/** 将房型详情转换为不包含价格字段的可编辑载荷。 */
export function toStayResourceSkuMutation(item: StayResourceSkuItem): StayResourceSkuMutation {
  return {
    skuName: item.skuName,
    roomType: item.roomType,
    roomUsageLabel: item.roomUsageLabel ?? "",
    roomCapacity: item.roomCapacity,
    remark: item.remark,
  };
}

/** 将供应能力详情转换为统一 PUT 所需的完整业务载荷。 */
export function toStayResourceSupplierMutation(
  item: StayResourceSupplierItem,
): StayResourceSupplierMutation {
  return {
    staySkuId: item.staySkuId,
    supplierId: item.supplierId,
    procurementChannelType: item.procurementChannelType,
    cooperationStatus: item.cooperationStatus,
    validFrom: item.validFrom,
    validTo: item.validTo,
    evidenceType: item.evidenceType,
    evidenceId: item.evidenceId,
    evidenceSummary: item.evidenceSummary,
    reviewStatus: item.reviewStatus === "approved" || item.reviewStatus === "rejected"
      ? item.reviewStatus
      : "pending",
    remark: item.remark,
  };
}

/** 通过统一能力 PUT 写入审核状态，不伪造独立审核路由。 */
export function reviewStayResourceSupplier(
  spuId: string,
  item: StayResourceSupplierItem,
  payload: StayResourceSupplierReviewMutation,
) {
  return updateStayResourceSupplier(spuId, item.capabilityId, {
    ...toStayResourceSupplierMutation(item),
    reviewStatus: payload.decision === "approve" ? "approved" : "rejected",
  });
}

/** 通过统一能力 PUT 将合作状态更新为 terminated。 */
export function deactivateStayResourceSupplier(
  spuId: string,
  item: StayResourceSupplierItem,
) {
  return updateStayResourceSupplier(spuId, item.capabilityId, {
    ...toStayResourceSupplierMutation(item),
    cooperationStatus: "terminated",
  });
}

/** 校验酒店主档表单；坐标不属于该载荷，无法从此链路直写。 */
export function validateStayResourceSpuMutation(
  payload: StayResourceSpuMutation,
): StayResourceMutationErrors {
  const errors: StayResourceMutationErrors = {};
  required(errors, "hotelName", payload.hotelName, "酒店名称为必填", 300);
  required(errors, "country", payload.country, "国家或地区为必填", 100);
  required(errors, "province", payload.province, "省份为必填", 100);
  required(errors, "city", payload.city, "城市为必填", 100);
  optional(errors, "hotelShortName", payload.hotelShortName, 150);
  optional(errors, "district", payload.district, 100);
  optional(errors, "address", payload.address, 500);
  optional(errors, "stayLevel", payload.stayLevel, 100);
  optional(errors, "hotelType", payload.hotelType, 100);
  optional(errors, "remark", payload.remark, 1000);
  if (payload.country.trim() === "中国" && !isValidChinaRegion(payload)) {
    errors.region = "中国省、市、区组合不正确";
  }
  return errors;
}

/** 校验房型表单，不接受任何价格字段。 */
export function validateStayResourceSkuMutation(
  payload: StayResourceSkuMutation,
): StayResourceMutationErrors {
  const errors: StayResourceMutationErrors = {};
  required(errors, "skuName", payload.skuName, "房型名称为必填", 300);
  required(errors, "roomType", payload.roomType, "物理房型为必填", 100);
  optional(errors, "roomUsageLabel", payload.roomUsageLabel, 100);
  optional(errors, "remark", payload.remark, 1000);
  if (payload.roomCapacity !== undefined
    && (!Number.isInteger(payload.roomCapacity) || payload.roomCapacity < 1)) {
    errors.roomCapacity = "入住人数必须为正整数";
  }
  return errors;
}

/** 校验酒店主档审核；驳回必须填写原因。 */
export function validateStayResourceReviewMutation(
  payload: StayResourceReviewMutation,
): StayResourceMutationErrors {
  const errors: StayResourceMutationErrors = {};
  if (payload.decision === "reject" && !payload.reason.trim()) {
    errors.reason = "驳回原因为必填";
  } else if (Array.from(payload.reason.trim()).length > 1000) {
    errors.reason = "审核原因不能超过 1000 个字符";
  }
  return errors;
}

/** 校验媒体元数据与显式房型范围，certificate 永远不可作为正常场景。 */
export function validateStayResourceMediaMutation(
  payload: StayResourceMediaMutation,
): StayResourceMutationErrors {
  const errors: StayResourceMutationErrors = {};
  required(errors, "mediaName", payload.mediaName, "媒体名称为必填", 300);
  required(errors, "fileUrl", payload.fileUrl, "请先上传文件", 1000);
  if (payload.mediaScene === "certificate" || !payload.mediaScene) {
    errors.mediaScene = "媒体场景不可用";
  }
  if (payload.staySkuId && payload.mediaScene !== "room") {
    errors.staySkuId = "只有房间图片可以指定房型范围";
  }
  if (payload.mediaScene === "room" && !payload.staySkuId?.trim()) {
    errors.staySkuId = "请选择关联房型";
  }
  if (!Number.isInteger(payload.sortNo) || payload.sortNo < 0) {
    errors.sortNo = "排序号必须为非负整数";
  }
  optional(errors, "contentText", payload.contentText, 1000);
  optional(errors, "remark", payload.remark, 1000);
  return errors;
}

export type StayResourceMediaGroup = {
  key: string;
  title: string;
  items: StayResourceMediaItem[];
};

/** 按酒店通用与房型作用域分组展示媒体，便于区分对应关系。 */
export function groupStayResourceMediaBySku(
  media: StayResourceMediaItem[],
  skus: StayResourceSkuItem[],
): StayResourceMediaGroup[] {
  const sorted = [...media].sort((left, right) => left.sortNo - right.sortNo);
  const skuNameById = new Map(skus.map((sku) => [sku.staySkuId, sku.skuName]));
  const hotelLevel = sorted.filter((item) => !item.staySkuId);
  const groups: StayResourceMediaGroup[] = [];
  if (hotelLevel.length) {
    groups.push({ key: "hotel", title: "酒店通用", items: hotelLevel });
  }
  for (const sku of skus) {
    const items = sorted.filter((item) => item.staySkuId === sku.staySkuId);
    if (!items.length) continue;
    groups.push({ key: sku.staySkuId, title: sku.skuName, items });
  }
  const knownSkuIds = new Set(skus.map((sku) => sku.staySkuId));
  const orphanSkuIds = [...new Set(
    sorted
      .map((item) => item.staySkuId)
      .filter((skuId): skuId is string => Boolean(skuId) && !knownSkuIds.has(skuId)),
  )];
  for (const skuId of orphanSkuIds) {
    groups.push({
      key: skuId,
      title: skuNameById.get(skuId) ?? skuId,
      items: sorted.filter((item) => item.staySkuId === skuId),
    });
  }
  return groups;
}

/** 校验 GEO 审核只引用已有候选，不接收经纬度。 */
export function validateStayResourceGeoReview(
  payload: StayResourceGeoReviewMutation,
): StayResourceMutationErrors {
  const errors: StayResourceMutationErrors = {};
  if (payload.decision === "approve" && !payload.candidateId?.trim()) {
    errors.candidateId = "审核通过前必须选择一个候选";
  }
  if (payload.reason.trim().length > 1000) {
    errors.reason = "审核原因不能超过 1000 个字符";
  }
  return errors;
}

/** 人工选择列表按 POI 去重，优先保留合格与第二阶段候选。 */
export function dedupeStayResourceGeoCandidates(
  candidates: StayResourceGeoCandidate[],
): StayResourceGeoCandidate[] {
  const preferred = new Map<string, StayResourceGeoCandidate>();
  const order: string[] = [];
  for (const candidate of candidates) {
    const key = candidate.poiId.trim() || candidate.candidateId;
    if (!preferred.has(key)) {
      order.push(key);
      preferred.set(key, candidate);
      continue;
    }
    const current = preferred.get(key);
    if (current && shouldPreferGeoCandidate(candidate, current)) {
      preferred.set(key, candidate);
    }
  }
  return order.map((key) => preferred.get(key)!);
}

function shouldPreferGeoCandidate(
  next: StayResourceGeoCandidate,
  current: StayResourceGeoCandidate,
): boolean {
  if (next.qualified !== current.qualified) {
    return next.qualified;
  }
  if (next.verificationStage !== current.verificationStage) {
    return next.verificationStage === "amap_llm_retry";
  }
  return (next.score ?? -1) > (current.score ?? -1);
}

/** 校验统一供应能力的渠道、有效期和证据字段。 */
export function validateStayResourceSupplierMutation(
  payload: StayResourceSupplierMutation,
): StayResourceMutationErrors {
  const errors: StayResourceMutationErrors = {};
  required(errors, "supplierId", payload.supplierId, "供应商 ID 为必填", 64);
  if (![
    "direct_resource_provider", "ground_operator", "other_intermediary", "unknown",
  ].includes(payload.procurementChannelType)) {
    errors.procurementChannelType = "采购渠道不正确";
  }
  if (!["candidate", "negotiating", "verified", "active", "paused", "terminated"]
    .includes(payload.cooperationStatus)) {
    errors.cooperationStatus = "合作状态不正确";
  }
  if (payload.validFrom && payload.validTo && payload.validFrom > payload.validTo) {
    errors.validTo = "有效期结束日期不能早于开始日期";
  }
  optional(errors, "evidenceType", payload.evidenceType, 100);
  optional(errors, "evidenceId", payload.evidenceId, 64);
  optional(errors, "evidenceSummary", payload.evidenceSummary, 1000);
  optional(errors, "remark", payload.remark, 1000);
  const hasEvidence = Boolean(
    payload.evidenceType?.trim()
      && (payload.evidenceId?.trim() || payload.evidenceSummary?.trim()),
  );
  if ((payload.cooperationStatus === "verified" || payload.cooperationStatus === "active")
    && (payload.reviewStatus !== "approved" || !hasEvidence)) {
    errors.cooperationStatus = "已核验或合作中的能力必须审核通过并具备证据";
  }
  if (payload.reviewStatus === "approved" && !hasEvidence) {
    errors.evidenceSummary = "审核通过前必须提供证据类型及证据内容";
  }
  return errors;
}

/** 将统一 HTTP 错误转换为稳定的住宿资源提示。 */
export function stayResourceMutationErrorMessage(error: unknown): string {
  if (!isHttpClientError(error)) {
    if (error instanceof Error && error.message === MULTI_SAME_HOTEL_GEO_CREATE_ERROR) {
      return MULTI_SAME_HOTEL_GEO_CREATE_ERROR;
    }
    if (error instanceof Error && error.message === GEO_REGION_MISMATCH_CREATE_ERROR) {
      return GEO_REGION_MISMATCH_CREATE_ERROR;
    }
    if (error instanceof Error && error.message.includes("住宿媒体仅支持")) {
      return "图片上传失败：文件校验未通过，请确认格式和内容。";
    }
    return "住宿资源操作失败，请稍后重试。";
  }
  if (isStayResourceMediaUploadError(error)) {
    switch (error.code) {
      case "MEDIA_CONFIG_MISSING":
        return "图片上传失败：媒体服务配置缺失，请检查存储配置后重试。";
      case "MEDIA_VALIDATION_ERROR":
        return error.message.includes("media-too-large")
          ? "图片上传失败：文件过大，请压缩后重试。"
          : "图片上传失败：文件校验未通过，请确认格式和内容。";
      case "PAYLOAD_TOO_LARGE":
        return "图片上传失败：文件过大，请压缩后重试。";
      case "MEDIA_PROVIDER_ERROR":
        return "图片上传失败：存储服务暂不可用，请稍后重试。";
      default:
        if (error.statusCode === 403) {
          return "图片上传失败：当前账号没有上传权限。";
        }
        return "图片上传失败，请稍后重试。";
    }
  }
  switch (error.code) {
    case "STAY_RESOURCE_DUPLICATE":
    case "STAY_RESOURCE_SKU_DUPLICATE":
    case "STAY_RESOURCE_CAPABILITY_CONFLICT":
      return error.message.trim() || "唯一信息或有效期冲突，请检查后重试。";
    case "STAY_RESOURCE_NOT_FOUND":
    case "STAY_RESOURCE_SKU_NOT_FOUND":
      return "酒店或房型不存在，数据可能已被更新。";
    case "STAY_RESOURCE_SKU_PARENT_MISMATCH":
      return "房型不属于当前酒店，请刷新后重试。";
    case "STAY_RESOURCE_GEO_CANDIDATE_INVALID":
      return "GEO 候选已失效或不合格，请重新核验。";
    case "STAY_RESOURCE_INVALID_STATE":
      return error.message.trim() || "当前状态不允许该操作，请刷新后重试。";
    case "ECONNABORTED":
    case "ERR_NETWORK":
      if (error.request.url.includes("/geo/verify") || /timeout/i.test(error.message)) {
        return "GEO 核验耗时较长已超时，请稍后刷新查看结果或再试一次。";
      }
      return "网络异常，请稍后重试。";
    default:
      if (/timeout/i.test(error.message)) {
        return "GEO 核验耗时较长已超时，请稍后刷新查看结果或再试一次。";
      }
      return error.message.trim() || "住宿资源操作失败，请稍后重试。";
  }
}

function isStayResourceMediaUploadError(error: { request: { url: string } }): boolean {
  return error.request.url.includes("/resource-stays/media/upload");
}

/** 检查中国省市区是否符合共享行政区划事实源。 */
function isValidChinaRegion(payload: StayResourceSpuMutation): boolean {
  const province = payload.province.trim();
  const city = payload.city.trim();
  const district = payload.district.trim();
  if (!province || !city || !getProvinceOptions().includes(province)) {
    return false;
  }
  if (!getCityOptions(province).includes(city)) {
    return false;
  }
  return !district || getDistrictOptions(province, city).includes(district);
}

function required(
  errors: StayResourceMutationErrors,
  field: string,
  value: string,
  emptyMessage: string,
  max: number,
) {
  const normalized = value.trim();
  if (!normalized) errors[field] = emptyMessage;
  else if (Array.from(normalized).length > max) errors[field] = `不能超过 ${max} 个字符`;
}

function optional(
  errors: StayResourceMutationErrors,
  field: string,
  value: string | undefined,
  max: number,
) {
  if (value && Array.from(value.trim()).length > max) errors[field] = `不能超过 ${max} 个字符`;
}
